import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { useSelector } from "react-redux";
import { ArrowDown, ArrowUp } from "lucide-react";
import { defaultGoal } from "../../reducers/goalReducer";
import ExportIcon from "../../icons/ExportIcon";
import ChartCard from "../components/ChartCard";
import GoalBarChart from "../components/charts/GoalBarChart";
import InputInlineWidget from "../components/InputInlineWidget";
import { getGoalSummary, GoalStatus } from "../../goals/GoalList";
import { filterDataGoalChart, getDateUnit, handleDataGoalChart } from "../DataUtils";
import { colors as defaultColors } from "../../board/components/TaskDetail/ColorPicker";
import { useAggregatedData } from "../hooks/useReportData";
import ChartLoading from "../components/ChartLoading";

const GoalWidget = ({
  projectId,
  widget,
  className,
  onClickUp,
  onClickDown,
  onClickExport,
  onClickSetting,
  onClickDelete,
  onUpdateTitle,
}) => {
  const { t, i18n } = useTranslation();

  const [goalFilters, setGoalFilters] = useState<any>(fromJS({}));

  const goals = useSelector((state: any) => state.goal.get("goals"));
  const currency = useSelector((state: any) => state.projectReducer.getIn(["project", "currency"]));
  const goal = goals.find((item) => item.get("id") == widget.data?.goalId) || fromJS(defaultGoal);

  const objectType = widget.data?.objectType || "SALE";
  const title = widget.data?.title;
  const colors = widget.data?.colors || [];
  const barColors = colors.length > 0 ? colors : defaultColors;

  const goalId = goal.get("id");
  const viewBy = "start";
  const segmentBy = "None";
  const dateUnit = getDateUnit(goal);

  const { data: dataAggregated, isLoading: isLoadingAggregated } = useAggregatedData(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    goalFilters,
    objectType,
    !!goalId
  );

  useEffect(() => {
    if (goalId) {
      const goal = goals.find((item) => item.get("id") == goalId) || fromJS(defaultGoal);
      filterDataGoalChart(projectId, goal).then((filters) => {
        setGoalFilters(filters);
      });
    }
  }, [projectId, goalId, goals]);

  return (
    <ChartCard
      title={
        <InputInlineWidget
          placeholder={t("e.g Deal Performance")}
          value={title}
          className="text-sm sm:text-sm/6 md:text-base font-medium text-gray-600 placeholder:text-gray-400"
          onFinish={onUpdateTitle}
        />
      }
      subtitle={
        <div className="text-blue-900 font-semibold flex items-center space-x-2">
          <GoalStatus goal={goal} /> <div>{getGoalSummary(goal, currency, t, i18n)}</div>
        </div>
      }
      className={className}
      isLoading={isLoadingAggregated}
      // left={
      //   <div className="hidden md:flex items-center gap-2">
      //     <div className="flex items-center gap-2 border border-gray-200 rounded-lg h-10 px-3 text-gray-700 bg-white">
      //       <span className="text-sm">01 Jan, 2024 - 31 Dec 2024</span>
      //     </div>
      //   </div>
      // }
      actions={[
        {
          label: t("Up"),
          content: <ArrowUp className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickUp(event),
        },
        {
          label: t("Down"),
          content: <ArrowDown className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickDown(event),
        },
        {
          label: t("Export"),
          content: <ExportIcon />,
          onClick: () => {
            filterDataGoalChart(projectId, goal).then((filters) => {
              onClickExport(filters);
            });
          },
        },
      ]}
      onSetting={onClickSetting}
      onDelete={onClickDelete}
    >
      {dataAggregated && !isLoadingAggregated ? (
        <GoalBarChart
          data={handleDataGoalChart(dataAggregated, goal, currency)}
          goalValue={goal.getIn(["trackingGoal", "value"])}
          barSize={30}
          barColors={barColors}
        />
      ) : (
        <ChartLoading />
      )}
    </ChartCard>
  );
};

export default GoalWidget;
